document.documentElement.classList.remove('no-js');
document.documentElement.classList.add('js');

class PawlidoStudioLanguage {
  constructor() {
    this.locale = document.documentElement.lang === 'ar' ? 'ar' : 'en';
    document.querySelectorAll('[data-ps-localization-select]').forEach(select => {
      select.addEventListener('change', () => {
        const form = select.form;
        let returnTo = form.querySelector('input[name="return_to"]');
        if (!returnTo) {
          returnTo = document.createElement('input');
          returnTo.type = 'hidden';
          returnTo.name = 'return_to';
          form.append(returnTo);
        }
        const publicUrl = new URL(window.location.href);
        if (['pawlido-preview', 'pawlido-catalog-preview'].includes(publicUrl.searchParams.get('view'))) publicUrl.searchParams.delete('view');
        returnTo.value = `${publicUrl.pathname}${publicUrl.search}${publicUrl.hash}`;
        form.submit();
      });
    });
    this.set(this.locale);
  }

  set(locale) {
    this.locale = locale;
    document.documentElement.lang = locale;
    document.documentElement.dir = locale === 'ar' ? 'rtl' : 'ltr';
    document.querySelectorAll('[data-ps-en][data-ps-ar]').forEach(element => {
      element.textContent = locale === 'ar' ? element.dataset.psAr : element.dataset.psEn;
    });
    document.querySelectorAll('[data-ps-alt-en][data-ps-alt-ar]').forEach(element => {
      element.alt = locale === 'ar' ? element.dataset.psAltAr : element.dataset.psAltEn;
    });
    document.querySelectorAll('[data-ps-aria-en][data-ps-aria-ar]').forEach(element => {
      element.setAttribute('aria-label', locale === 'ar' ? element.dataset.psAriaAr : element.dataset.psAriaEn);
    });
    document.querySelectorAll('[data-ps-scope]').forEach(scope => {
      scope.dir = locale === 'ar' ? 'rtl' : 'ltr';
    });
    document.dispatchEvent(new CustomEvent('pawlido:language', { detail: { locale } }));
  }
}

class PawlidoHomeStudio extends HTMLElement {
  connectedCallback() {
    this.locale = document.documentElement.lang === 'ar' ? 'ar' : 'en';
    const chapterData = this.querySelector('[data-chapters]');
    this.chapters = chapterData ? JSON.parse(chapterData.textContent) : {};
    this.querySelectorAll('[data-chapter]').forEach(button => {
      button.addEventListener('click', () => this.showChapter(button.dataset.chapter));
      button.addEventListener('keydown', event => this.moveChapterFocus(event, button));
    });
    this.querySelectorAll('[data-pet]').forEach(button => button.addEventListener('click', () => {
      this.pet = button.dataset.pet;
      this.querySelectorAll('[data-pet]').forEach(item => item.setAttribute('aria-pressed', String(item === button)));
      this.querySelector('[data-finder-step-one]').hidden = true;
      this.querySelector('[data-finder-step-two]').hidden = false;
      this.querySelector('[data-finder-step-two] .ps-choice')?.focus();
    }));
    this.querySelectorAll('[data-need]').forEach(button => {
      button.addEventListener('click', () => this.showResult(button.dataset.need));
    });
    this.querySelector('[data-finder-reset]')?.addEventListener('click', () => this.resetFinder());
    document.addEventListener('pawlido:language', event => {
      this.locale = event.detail.locale;
      this.showChapter(this.activeChapter || 'morning');
      if (this.currentNeed) this.showResult(this.currentNeed, { moveFocus: false });
    });
    this.showChapter('morning');
  }

  showChapter(key) {
    this.activeChapter = key;
    const chapter = this.chapters[key];
    if (!chapter) return;
    this.querySelectorAll('[data-chapter]').forEach(button => {
      const active = button.dataset.chapter === key;
      button.setAttribute('aria-selected', String(active));
      button.tabIndex = active ? 0 : -1;
      if (active) this.querySelector('[role="tabpanel"]')?.setAttribute('aria-labelledby', button.id);
    });
    const image = this.querySelector('[data-chapter-image]');
    if (image) {
      image.src = chapter.image;
      image.alt = this.locale === 'ar' ? chapter.altAr : chapter.altEn;
    }
    this.querySelector('[data-chapter-title]').textContent = this.locale === 'ar' ? chapter.titleAr : chapter.titleEn;
    this.querySelector('[data-chapter-copy]').textContent = this.locale === 'ar' ? chapter.copyAr : chapter.copyEn;
    const link = this.querySelector('[data-chapter-link]');
    link.href = chapter.url;
    link.textContent = this.locale === 'ar' ? chapter.linkAr : chapter.linkEn;
  }

  moveChapterFocus(event, current) {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const tabs = [...this.querySelectorAll('[data-chapter]')];
    let index = tabs.indexOf(current);
    if (event.key === 'Home') index = 0;
    else if (event.key === 'End') index = tabs.length - 1;
    else index = (index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
    tabs[index].focus();
    this.showChapter(tabs[index].dataset.chapter);
  }

  showResult(need, options = {}) {
    this.currentNeed = need;
    const recommendations = {
      feeding: {
        titleEn: 'Start with meals and water', titleAr: 'ابدأ بالطعام والماء',
        copyEn: `Browse bowls, fountains and feeding tools across the category, then confirm the exact configuration on the product page.`,
        copyAr: `تصفّح أوعية الطعام والنوافير وأدوات التغذية ضمن القسم، ثم أكّد الخيار الدقيق في صفحة المنتج.`,
        url: this.dataset.feedingUrl
      },
      play: {
        titleEn: 'Make room for play', titleAr: 'اختر وقتاً للعب',
        copyEn: `Explore play and enrichment across the category, with size and supervision details checked on each product page.`,
        copyAr: `استكشف ألعاب الحركة والتحفيز ضمن القسم، مع مراجعة المقاس وإرشادات الإشراف في صفحة كل منتج.`,
        url: this.dataset.toysUrl
      },
      care: {
        titleEn: 'Build a simpler care routine', titleAr: 'بسّط روتين العناية',
        copyEn: `Browse grooming and cleaning tools across the category, then review materials, fit and use instructions before choosing.`,
        copyAr: `تصفّح أدوات العناية والتنظيف ضمن القسم، ثم راجع المواد والمقاس وتعليمات الاستخدام قبل الاختيار.`,
        url: this.dataset.groomingUrl
      },
      travel: {
        titleEn: 'Prepare for walks and travel', titleAr: 'استعد للمشي والسفر',
        copyEn: `Browse travel carriers, and confirm measurements and compatibility before purchase.`,
        copyAr: `تصفّح حاملات السفر، وأكّد القياسات والتوافق قبل الشراء.`,
        url: this.dataset.travelUrl
      }
    };
    const recommendation = recommendations[need] || recommendations.feeding;
    this.querySelector('[data-finder-step-two]').hidden = true;
    const result = this.querySelector('[data-finder-result]');
    result.hidden = false;
    result.querySelector('[data-result-title]').textContent = this.locale === 'ar' ? recommendation.titleAr : recommendation.titleEn;
    result.querySelector('[data-result-copy]').textContent = this.locale === 'ar' ? recommendation.copyAr : recommendation.copyEn;
    result.querySelector('[data-result-link]').href = recommendation.url;
    if (options.moveFocus !== false) result.querySelector('[data-result-link]').focus();
  }

  resetFinder() {
    this.querySelector('[data-finder-step-one]').hidden = false;
    this.querySelector('[data-finder-step-two]').hidden = true;
    this.querySelector('[data-finder-result]').hidden = true;
    this.currentNeed = null;
    this.pet = null;
    this.querySelectorAll('.ps-choice').forEach(button => button.setAttribute('aria-pressed', 'false'));
    this.querySelector('[data-finder-step-one] .ps-choice')?.focus();
  }
}

class PawlidoFeederStudio extends HTMLElement {
  connectedCallback() {
    this.locale = document.documentElement.lang === 'ar' ? 'ar' : 'en';
    this.select = this.querySelector('[data-variant-select]');
    this.querySelectorAll('[data-version]').forEach(button => {
      button.addEventListener('click', () => this.chooseVersion(button.dataset.version));
    });
    this.select?.addEventListener('change', () => this.syncVariant());
    document.addEventListener('pawlido:language', event => {
      this.locale = event.detail.locale;
      this.syncVariant({ preserveUrl: true });
    });
    this.syncVariant({ preserveUrl: true });
  }

  chooseVersion(version) {
    const currentColor = this.select?.selectedOptions[0]?.dataset.color;
    const options = [...(this.select?.options || [])].filter(item => item.dataset.version === version);
    const option = options.find(item => item.dataset.color === currentColor && !item.disabled) || options.find(item => !item.disabled) || options[0];
    if (!option) return;
    this.select.value = option.value;
    this.syncVariant();
  }

  syncVariant(options = {}) {
    if (!this.select) return;
    const option = this.select.selectedOptions[0];
    const version = option.dataset.version || 'unknown';
    this.querySelectorAll('[data-version]').forEach(button => {
      button.setAttribute('aria-pressed', String(button.dataset.version === version));
    });
    this.querySelectorAll('[data-product-price]').forEach(element => {
      const money = document.createElement('span');
      money.className = 'money';
      money.dataset.amount = option.dataset.amount;
      money.dataset.currency = option.dataset.currency;
      money.textContent = option.dataset.priceText;
      element.dataset.amount = option.dataset.amount;
      element.dataset.currency = option.dataset.currency;
      element.replaceChildren(money);
    });
    window.requestAnimationFrame(() => {
      if (typeof window.bucksCC?.reConvert === 'function') window.bucksCC.reConvert();
    });
    const image = this.querySelector('[data-product-image]');
    if (image) {
      image.src = option.dataset.image || image.dataset.fallbackImage || image.src;
      image.alt = this.locale === 'ar'
        ? 'صورة موزّع Pawlido مشتركة بين الإصدارات'
        : 'Pawlido feeder image shared across versions';
    }
    if (!options.preserveUrl) {
      const url = new URL(window.location.href);
      if (['pawlido-preview', 'pawlido-catalog-preview'].includes(url.searchParams.get('view'))) url.searchParams.delete('view');
      url.searchParams.set('variant', option.value);
      window.history.replaceState({}, '', url);
    }
    const claims = {
      key: { en: ['Button programming on the unit', 'Scheduled dry-food portions', 'No WiFi and no camera'], ar: ['برمجة بالأزرار على الجهاز', 'حصص طعام جاف مجدولة', 'من دون WiFi ومن دون كاميرا'] },
      app: { en: ['WiFi app control', 'Remote scheduling and dispensing', 'Voice recording'], ar: ['تحكم عبر التطبيق وWiFi', 'جدولة وصرف الطعام عن بعد', 'تسجيل صوتي'] },
      video: { en: ['App control', 'Built-in camera', 'Video version shown separately'], ar: ['تحكم عبر التطبيق', 'كاميرا مدمجة', 'نسخة الفيديو موضحة بشكل مستقل'] },
      unknown: { en: ['Review the exact variant details before adding to cart'], ar: ['راجع تفاصيل الخيار المحدد قبل إضافته إلى السلة'] }
    };
    const values = claims[version]?.[this.locale] || claims.unknown.en;
    const featureList = this.querySelector('[data-version-features]');
    featureList?.replaceChildren(...values.map(value => {
      const item = document.createElement('li');
      item.textContent = value;
      return item;
    }));
    const add = this.querySelector('[data-add-button]');
    if (add) {
      add.disabled = option.disabled;
      add.textContent = option.disabled
        ? (this.locale === 'ar' ? 'غير متوفر' : 'Unavailable')
        : (this.locale === 'ar' ? 'أضف إلى السلة' : 'Add to cart');
    }
  }
}

class PawlidoGenericProduct extends HTMLElement {
  connectedCallback() {
    this.locale = document.documentElement.lang === 'ar' ? 'ar' : 'en';
    this.select = this.querySelector('[data-ps-generic-variant]');
    this.choiceButtons = Array.from(this.querySelectorAll('[data-ps-pw2673-choice]'));
    if (this.choiceButtons.length) {
      this.classList.add('ps-options-enhanced');
      this.choiceButtons.forEach(button => button.addEventListener('click', () => {
        if (!this.select || button.disabled) return;
        this.select.value = button.dataset.psPw2673Choice;
        this.select.dispatchEvent(new Event('change', { bubbles: true }));
      }));
    }
    this.galleryButtons = Array.from(this.querySelectorAll('[data-ps-gallery-image]'));
    this.galleryButtons.forEach(button => button.addEventListener('click', () => {
      const image = this.querySelector('.ps-generic-main-image');
      if (!image) return;
      if (button.dataset.psGallerySrcset) image.srcset = button.dataset.psGallerySrcset;
      if (button.dataset.psGallerySizes) image.sizes = button.dataset.psGallerySizes;
      image.src = button.dataset.psGalleryImage;
      image.alt = button.dataset.psGalleryAlt || '';
      this.syncGallery(image.src);
      const status = this.querySelector('[data-ps-media-status]');
      const option = this.select?.selectedOptions[0];
      if (status) status.hidden = option?.dataset.imageSpecific === 'true' && this.galleryPath(image.src) === this.galleryPath(option.dataset.image);
    }));
    this.syncGallery(this.querySelector('.ps-generic-main-image')?.src);
    this.select?.addEventListener('change', () => this.syncVariant());
    document.addEventListener('pawlido:language', event => {
      this.locale = event.detail.locale;
      this.syncVariant({ preserveUrl: true });
    });
    this.syncVariant({ preserveUrl: true });
  }

  galleryPath(url) {
    return url ? new URL(url, window.location.href).pathname : '';
  }

  syncGallery(url) {
    const current = this.galleryPath(url);
    this.galleryButtons?.forEach(button => button.setAttribute('aria-pressed', String(this.galleryPath(button.dataset.psGalleryImage) === current)));
  }

  replaceMoney(container, amount, currency, fallbackText, strike = false) {
    if (!container) return;
    if (!fallbackText) {
      container.hidden = true;
      container.replaceChildren();
      return;
    }
    const money = document.createElement('span');
    money.className = 'money';
    money.dataset.amount = amount;
    money.dataset.currency = currency;
    money.textContent = fallbackText;
    container.hidden = false;
    if (strike) {
      const strikeElement = document.createElement('s');
      strikeElement.append(money);
      container.replaceChildren(strikeElement);
    } else {
      container.replaceChildren(money);
    }
  }

  syncVariant(options = {}) {
    if (!this.select) return;
    const option = this.select.selectedOptions[0];
    if (!option || !option.value) {
      this.choiceButtons?.forEach(button => button.setAttribute('aria-pressed', 'false'));
      const selectedRow = this.querySelector('[data-ps-selected-row]');
      if (selectedRow) selectedRow.hidden = true;
      const addButton = this.querySelector('[data-ps-generic-add]');
      if (addButton) {
        addButton.disabled = true;
        addButton.textContent = this.locale === 'ar' ? 'اختر خيارًا' : 'Choose an option';
      }
      return;
    }
    this.replaceMoney(this.querySelector('[data-ps-generic-price]'), option.dataset.amount, option.dataset.currency, option.dataset.priceText);
    this.replaceMoney(this.querySelector('[data-ps-generic-compare]'), option.dataset.compareAmount || '', option.dataset.currency, option.dataset.compareText, true);
    this.choiceButtons?.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.psPw2673Choice === option.value)));
    const selectedRow = this.querySelector('[data-ps-selected-row]');
    if (selectedRow) selectedRow.hidden = false;
    const selectedLabel = this.querySelector('[data-ps-selected-variant]');
    if (selectedLabel) selectedLabel.textContent = option.dataset.title;
    const selectionNote = this.querySelector('[data-ps-selection-note]');
    const exactAccessoryNotice = this.querySelector('[data-ps-exact-accessory]');
    if (exactAccessoryNotice) exactAccessoryNotice.hidden = true;
    if (selectionNote) {
      const accessory = option.dataset.kind === 'accessory';
      const needsReview = option.dataset.kind === 'review';
      selectionNote.dataset.kind = needsReview ? 'review' : (accessory ? 'accessory' : 'configuration');
      selectionNote.classList.toggle('ps-selection-note--accessory', accessory || needsReview);
      selectionNote.hidden = false;
      const exactAccessoryCopy = {
        '43003541913690': {
          en: 'This option is labelled Bracket in the current catalogue. Contents and compatibility are pending supplier confirmation; do not assume the odor-removal machine is included.',
          ar: 'هذا الخيار مسمّى Bracket في الكتالوج الحالي. المحتويات والتوافق بانتظار تأكيد المورد؛ لا تفترض أن جهاز إزالة الروائح مشمول.'
        },
        '43945192849498': {
          en: 'Replacement-filter option only. Fountain not included.',
          ar: 'فلاتر بديلة فقط. النافورة غير مشمولة.'
        }
      };
      const exactCopy = exactAccessoryCopy[option.value];
      selectionNote.textContent = (accessory || needsReview)
        ? (exactCopy ? exactCopy[this.locale] : (this.locale === 'ar'
          ? 'الخيار المحدد ملحق أو قطعة بديلة، وليس المنتج الكامل. أكّد التوافق وعدد القطع في تفاصيل المنتج.'
          : 'This selected option is an accessory or replacement part, not the complete product. Confirm compatibility and piece count in the product details.'))
        : (this.locale === 'ar'
          ? 'ينطبق السعر وعنصر السلة على الخيار المحدد بدقة.'
          : 'The price and cart item apply to the exact selected option.');
    }
    const hidesMerchantDescription = option.dataset.kind === 'accessory' || option.dataset.kind === 'review';
    const merchantDescription = this.querySelector('[data-ps-merchant-description]');
    const configurationDetails = this.querySelector('[data-ps-configuration-details]');
    if (merchantDescription) merchantDescription.hidden = hidesMerchantDescription;
    if (configurationDetails) configurationDetails.hidden = !hidesMerchantDescription;
    const image = this.querySelector('.ps-generic-main-image');
    if (image && option.dataset.image) {
      if (option.dataset.imageSrcset) image.srcset = option.dataset.imageSrcset;
      if (option.dataset.imageSizes) image.sizes = option.dataset.imageSizes;
      image.src = option.dataset.image;
      image.alt = option.dataset.imageAlt || option.dataset.title;
    }
    this.syncGallery(image?.src);
    const mediaStatus = this.querySelector('[data-ps-media-status]');
    if (mediaStatus) mediaStatus.hidden = option.dataset.imageSpecific === 'true';
    const addButton = this.querySelector('[data-ps-generic-add]');
    if (addButton) {
      const available = option.dataset.available === 'true';
      addButton.disabled = !available;
      addButton.textContent = available
        ? (this.locale === 'ar' ? 'أضف إلى السلة' : 'Add to cart')
        : (this.locale === 'ar' ? 'غير متوفر' : 'Unavailable');
    }
    if (!options.preserveUrl) {
      const url = new URL(window.location.href);
      if (['pawlido-preview', 'pawlido-catalog-preview'].includes(url.searchParams.get('view'))) url.searchParams.delete('view');
      url.searchParams.set('variant', option.value);
      window.history.replaceState({}, '', url);
    }
    window.requestAnimationFrame(() => {
      if (typeof window.bucksCC?.reConvert === 'function') window.bucksCC.reConvert();
    });
  }
}

class PawlidoCatalogControls {
  constructor() {
    document.querySelectorAll('[data-ps-auto-submit]').forEach(control => {
      control.addEventListener('change', () => control.form?.requestSubmit());
    });
    document.querySelectorAll('[data-ps-facets]').forEach(panel => {
      const openButton = document.querySelector(`[data-ps-facets-open][aria-controls="${panel.id}"]`);
      const closeButton = panel.querySelector('[data-ps-facets-close]');
      let backdrop;
      const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';
      const close = () => {
        panel.classList.remove('is-open');
        panel.removeAttribute('role');
        panel.removeAttribute('aria-modal');
        document.body.classList.remove('ps-facets-open');
        backdrop?.remove();
        backdrop = null;
        openButton?.setAttribute('aria-expanded', 'false');
        openButton?.focus();
      };
      openButton?.addEventListener('click', () => {
        panel.classList.add('is-open');
        panel.setAttribute('role', 'dialog');
        panel.setAttribute('aria-modal', 'true');
        document.body.classList.add('ps-facets-open');
        openButton.setAttribute('aria-expanded', 'true');
        backdrop = document.createElement('button');
        backdrop.type = 'button';
        backdrop.className = 'ps-facets-backdrop';
        backdrop.setAttribute('aria-label', document.documentElement.lang === 'ar' ? 'إغلاق الفلاتر' : 'Close filters');
        backdrop.addEventListener('click', close);
        document.body.append(backdrop);
        closeButton?.focus();
      });
      closeButton?.addEventListener('click', close);
      panel.addEventListener('keydown', event => {
        if (event.key === 'Escape') close();
        if (event.key !== 'Tab') return;
        const focusable = [...panel.querySelectorAll(focusableSelector)].filter(element => element.offsetParent !== null);
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });
    });
  }
}

if (!customElements.get('pawlido-home-studio')) customElements.define('pawlido-home-studio', PawlidoHomeStudio);
if (!customElements.get('pawlido-feeder-studio')) customElements.define('pawlido-feeder-studio', PawlidoFeederStudio);
if (!customElements.get('pawlido-generic-product')) customElements.define('pawlido-generic-product', PawlidoGenericProduct);
new PawlidoStudioLanguage();
new PawlidoCatalogControls();


// Native disclosure remains usable without JavaScript; enhance dismissal only.
document.querySelectorAll('[data-ps-pet-menu]').forEach((menu) => {
  const trigger = menu.querySelector('summary');
  menu.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && menu.open) {
      event.preventDefault();
      menu.open = false;
      trigger.focus();
    }
  });
  document.addEventListener('click', (event) => {
    if (menu.open && !menu.contains(event.target)) menu.open = false;
  });
  menu.addEventListener('focusout', (event) => {
    if (event.relatedTarget && !menu.contains(event.relatedTarget)) menu.open = false;
  });
});


// Native product-card POSTs: prevent duplicate submits; no AJAX retry.
document.querySelectorAll('[data-ps-card-add]').forEach((form) => {
  form.addEventListener('submit', (event) => {
    if (form.dataset.psSubmitting === 'true') {
      event.preventDefault();
      return;
    }
    form.dataset.psSubmitting = 'true';
    form.setAttribute('aria-busy', 'true');
    const button = form.querySelector('[data-ps-card-submit]');
    button.disabled = true;
    button.querySelector('[data-ps-card-idle]').hidden = true;
    button.querySelector('[data-ps-card-loading]').hidden = false;
  });
});
window.addEventListener('pageshow', () => {
  document.querySelectorAll('[data-ps-card-add]').forEach((form) => {
    delete form.dataset.psSubmitting;
    form.removeAttribute('aria-busy');
    const button = form.querySelector('[data-ps-card-submit]');
    button.disabled = false;
    button.querySelector('[data-ps-card-idle]').hidden = false;
    button.querySelector('[data-ps-card-loading]').hidden = true;
  });
});
